// Imports
import {rerenderValidationNew, validateField, validateForm} from "../utils/validation";
import {API_REQUEST_VALIDATION_RULES} from "../validation/apiRequestValidation";
import {getApiKeyFormWhitelist} from "../form/validation";
import {getRecaptchaToken} from "../utils/recaptcha";
import {requestApiKey} from "../api/requestApiKey";
import {bindModalTriggerLinks, initializeConfirmRegisterModal} from "../_components/authentication";
import {loadAndShowModal} from "../_components/modal";
import {showToast} from "../_components/toast";
import {trans} from "../i18n/i18n";
import {Toast} from "../consts/toast";
import {route} from "ziggy-js";
import {trackEvent} from "../tracking/gtm";
import {initBusinessIndustryFields} from "../form/businessIndustry";

// CONSTANTS
const API_KEY_REQUEST_MESSAGE_SELECTOR = ".js-formSubmitMessage";
const API_KEY_TXT_SELECTOR = ".js-apiKeyTxt";
const SECTION_API_ACCOUNT_READY_SELECTOR = ".js-sectionApiAccountReady";
const SECTION_API_KEY_REQUEST_SELECTOR = ".js-sectionApiRequest";
const REQUEST_FREE_TRIAL_BUTTON_SELECTOR = "requestFreeTrialButton";
const API_KEY_REQUEST_FORM_SELECTOR = "js-requestApiKey";
const SUBMIT_BUTTON_SELECTOR = "#requestApiKeySubmitBtn";
const SUBMIT_SPINNER_SELECTOR = "#apiKeyRequestSpinner";

document.addEventListener("DOMContentLoaded", init);

let businessFieldManager = {
  resolveInput(input) {
    return {targetInput: input, displayInput: input};
  },
  getDisplayInput() {
    return null;
  },
};

/**
 * Initializes all necessary event listeners on page load.
 * Currently sets up tracking for the "Request Free Trial" button.
 */
function init() {
  const freeTrialBtn = document.getElementById(REQUEST_FREE_TRIAL_BUTTON_SELECTOR);
  const apiRequestForm = document.getElementsByClassName(API_KEY_REQUEST_FORM_SELECTOR)[0];

  initFreeTrialTracking(freeTrialBtn);
  initApiRequestForm(apiRequestForm);
}

/**
 * Initializes the free trial button click listener and pushes an event to the dataLayer.
 * Intended for tracking user interactions with the "Request Free Trial" button via Google Tag Manager.
 *
 * @param {HTMLElement|null} buttonElement - The DOM element representing the free trial button.
 */
function initFreeTrialTracking(buttonElement) {
  if (!buttonElement) return;

  buttonElement.addEventListener("click", () => {
    trackEvent(
      "request_free_trial",
      {
        api_variant: "unknown",
        position: "under_api_test",
      }
    );
  });
}

/**
 * Initializes the API request form by attaching event listeners
 * for validation and form submission.
 */
function initApiRequestForm(apiRequestFormElement) {
  if (!apiRequestFormElement) return;

  businessFieldManager = initBusinessIndustryFields(apiRequestFormElement);

  const handleFormInput = (event) => handleApiRequestFormInput(event, businessFieldManager);

  apiRequestFormElement.addEventListener("change", handleFormInput);
  apiRequestFormElement.addEventListener("input", handleFormInput);
  apiRequestFormElement.addEventListener("submit", submitApiRequestFormAction);
}

/**
 * Validate form and check if there are any errors
 * then render results
 * @param e
 */
function handleApiRequestFormInput(e, manager = businessFieldManager) {
  const inputEl = e.target;
  const form = e.currentTarget || inputEl.closest("form");

  const {targetInput, displayInput} = manager.resolveInput(inputEl);

  if (!targetInput || !displayInput) {
    return;
  }

  const whitelist = getApiKeyFormWhitelist(isNewUser(form));
  const allErrors = validateField(form, targetInput, API_REQUEST_VALIDATION_RULES, whitelist);
  const fieldErrors = allErrors?.[targetInput.name] || [];

  rerenderValidationNew(displayInput, fieldErrors);
}

/**
 * Handles the submission of the API key request form.
 *
 * Prevents default form submission, validates user input, executes reCAPTCHA,
 * sends the request to the backend, and handles both success and error responses.
 * Relies on modular helpers to separate responsibilities like validation, API interaction,
 * feedback rendering, and reCAPTCHA token generation.
 *
 * @param {SubmitEvent} e - The form submit event.
 */
async function submitApiRequestFormAction(e) {
  e.preventDefault();
  const form = getFormAndCheck();
  if (!form) return;

  const whitelist = getApiKeyFormWhitelist(isNewUser(form));
  const hasErrors = validateForm(form, API_REQUEST_VALIDATION_RULES, whitelist);

  if (hasErrors) {
    const message = trans("Please correct the errors in the form before submitting.");
    renderMessageBox(message, Toast.ERROR);
    showToast(message, Toast.ERROR);
    return;
  }

  showSpinnerAndDisableBtn();

  try {
    const formData = await prepareFormData(form);
    const data = await requestApiKey(formData);
    handleApiKeySuccess(data);

    const emailVerValue = document.body.dataset.emailVerificationRequired;
    const successAlert = document.getElementsByClassName('js-section-VerifyEmail')[0];
    successAlert.classList.remove('d-none');

    // Scroll on top of the request api section
    const scrollTarget = document.getElementById('request-free-trial');
    if (scrollTarget) {
      const yOffset = -50;
      const y = scrollTarget.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }

    if (data.new_user || emailVerValue === 'true') {
      await loadAndShowModal('confirmEmail', {
        queryParams: {email: data.email || ""},
        onLoad: () => {
          initializeConfirmRegisterModal({redirectAfterConfirm: route('api.latest.docs')});
          bindModalTriggerLinks();
        }
      });
    }
  } catch (error) {
    handleApiKeyError(error);
  } finally {
    cleanupAfterSubmit(form);
    hideSpinnerAndEnableBtn();
  }
}


/**
 * Retrieves the API key request form from the DOM and checks for its existence.
 *
 * Logs a warning if the form is not found.
 *
 * @returns {HTMLFormElement | null} The form element if found, otherwise null.
 */
function getFormAndCheck() {
  const form = document.querySelector(`.${API_KEY_REQUEST_FORM_SELECTOR}`);
  if (!form) {
    console.warn("API request form not found.");
    return null;
  }
  return form;
}

/**
 * Determines whether the form is intended for a new user (registration).
 *
 * Checks for the presence of a password field to distinguish between
 * existing and new users.
 *
 * @param {HTMLFormElement} form - The form element to inspect.
 * @returns {boolean} True if the form contains a password field, indicating a new user.
 */
function isNewUser(form) {
  return !!form.querySelector("[name=password]");
}

/**
 * Prepares form data for submission by collecting inputs and appending a reCAPTCHA token if missing.
 *
 * Converts the form into a FormData object, checks for the presence of the "recaptcha_token",
 * and appends it using the reCAPTCHA v3 API if not already set. The result is returned
 * as a URLSearchParams instance suitable for Axios POST requests.
 *
 * @param {HTMLFormElement} form - The form element whose data should be serialized.
 * @returns {Promise<URLSearchParams>} A promise resolving to serialized form data with reCAPTCHA token included.
 */
async function prepareFormData(form) {
  const formData = new FormData(form);

  if (!formData.has("recaptcha_token")) {
    const recaptchaAction = form.dataset.recaptchaActionAjax;
    const token = await getRecaptchaToken(recaptchaAction);
    formData.append("recaptcha_token", token);
  }

  return formData;
}

/**
 * Handles the successful API key request response.
 *
 * Displays the received API key in the UI, removes the request form,
 * and triggers tracking events or account ready logic depending on user type.
 *
 * @param {Object} data - The response data from the API.
 * @param {string} [data.api_key] - The generated API key.
 * @param {boolean} [data.new_user] - Indicates if the user is newly registered.
 */
function handleApiKeySuccess(data) {
  document.querySelector(API_KEY_TXT_SELECTOR).innerHTML = data.api_key || "";
  document.querySelector(SECTION_API_KEY_REQUEST_SELECTOR)?.remove();

  if (data.new_user) {
    trackEvent("api_form_sent");
  } else {
    apiAccountReady();
  }
}

/**
 * Handles errors that occur during the API key request process.
 *
 * Renders user-facing error messages and triggers field-specific validation
 * feedback if provided by the server. Also handles CSRF expiration errors.
 *
 * @param {Object} error - The error object from Axios.
 * @param {Object} [error.response] - The Axios error response object.
 * @param {number} [error.response.status] - HTTP status code.
 * @param {Object} [error.response.data] - The response body containing error messages.
 */
function handleApiKeyError(error) {
  const data = error?.response?.data || {};
  if (data?.errors) {
    Object.entries(data.errors).forEach(([field, msgs]) => {
      const messages = Array.isArray(msgs) ? msgs : [msgs];
      const prefixedInput = document.getElementById(`requestApiKeyForm-${field}`);
      const displayInput = prefixedInput || businessFieldManager.getDisplayInput(field) || document.getElementById(field);

      if (displayInput) {
        rerenderValidationNew(displayInput, messages);
      }
    });
  }
}

/**
 * Performs post-submission cleanup of the API key form.
 *
 * Removes the reCAPTCHA token input from the form and hides any active loading spinner.
 *
 * @param {HTMLFormElement} form - The form element to clean up.
 */
function cleanupAfterSubmit(form) {
  form.querySelector("[name=recaptcha_token]")?.remove();
}

/**
 * Render a message (error, success, info) in the alert box
 * @param {string} message
 * @param {'success' | 'error' | 'info'} type
 */
function renderMessageBox(message, type = 'info') {
  const box = document.querySelector(API_KEY_REQUEST_MESSAGE_SELECTOR);
  if (!box) return;

  box.innerHTML = message;
  box.classList.remove('d-none', 'alert-danger', 'alert-success', 'alert-info');

  switch (type) {
    case 'success':
      box.classList.add('alert-success');
      break;
    case 'error':
      box.classList.add('alert-danger');
      break;
    case 'info':
    default:
      box.classList.add('alert-info');
      break;
  }
}

/**
 * Api account ready action
 */
function apiAccountReady() {
  const selector = document.querySelector(SECTION_API_ACCOUNT_READY_SELECTOR);
  if (!selector) return;

  const url = selector.getAttribute("data-url");

  selector.classList.remove("d-none");

  if (url) {
    setTimeout(() => {
      document.location.href = url;
    }, 500);
  }
}

function showSpinnerAndDisableBtn() {
  const btn = document.querySelector(SUBMIT_BUTTON_SELECTOR);
  const spinner = document.querySelector(SUBMIT_SPINNER_SELECTOR);

  if (btn) btn.setAttribute("disabled", "disabled");
  if (spinner) spinner.classList.remove("d-none");
}

function hideSpinnerAndEnableBtn() {
  const btn = document.querySelector(SUBMIT_BUTTON_SELECTOR);
  const spinner = document.querySelector(SUBMIT_SPINNER_SELECTOR);

  if (btn) btn.removeAttribute("disabled");
  if (spinner) spinner.classList.add("d-none");
}
